import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  FolderOpen,
  MessageSquare,
  Calendar,
  CalendarDays,
  Bell,
  Briefcase,
  Users,
  UsersRound,
  Shield,
  FileText,
  Settings,
  Bug,
  Lightbulb,
  Headphones,
  MoreHorizontal,
  NotebookText,
  X,
  AlertTriangle,
  Clock,
  ClipboardCheck,
  Package,
  HandCoins,
  DraftingCompass,
  FileQuestionMark,
  Tractor,
  ShieldAlert,
  Orbit,
  Box,
  Landmark,
  Receipt,
  Coins,
  Wallet,
  IdCard,
  ShieldCheck,
  PackageSearch,
  Sparkles,
  ArrowLeftRight,
  Calculator,
  Megaphone,
  Home,
  Files,
  FileCheck,
  TrendingUp,
  Hourglass,
  FileSignature,
  GraduationCap,
  CircleDollarSign,
  LineChart,
  Layers,
  Scale,
  Presentation,
  Settings2,
  PackageCheck,
  HardHat,
  GitBranch,
  FileKey,
  Eye,
  Palmtree,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useProject } from '../../hooks/useProject';
import { useMenuNotifications } from '../../hooks/useMenuNotifications';
import { hasPermission } from '../../lib/permissions';
import { MOBILE_NAV_GROUPS } from './navGroups';
import { useEffect, useRef, useState } from 'react';

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  badgeKey?: string;
  disabled?: boolean;
  testId?: string;
  permission?: { module: string; action: string };
  /** Sabit rol kısıtı (backend sabit rol kontrolü yapan modüller için, örn. AI asistanı). */
  fixedRoles?: string[];
}

const mainItems: NavItem[] = [
  { to: '/dashboard', label: 'Ana Sayfa', icon: LayoutDashboard, testId: 'nav-dashboard', permission: { module: 'dashboard', action: 'view' } },
  { to: '/tasks', label: 'Görevler', icon: ClipboardList, badgeKey: 'tasks', testId: 'nav-tasks', permission: { module: 'tasks', action: 'view' } },
  { to: '/files', label: 'Dosyalar', icon: FolderOpen, badgeKey: 'files', testId: 'nav-files', permission: { module: 'files', action: 'view' } },
  { to: '/messages', label: 'Mesajlar', icon: MessageSquare, badgeKey: 'messages', testId: 'nav-messages', permission: { module: 'messages', action: 'view' } },
  { to: '/calendar', label: 'Takvim', icon: Calendar, badgeKey: 'calendar', testId: 'nav-calendar', permission: { module: 'calendar', action: 'view' } },
];

const moreItemsBase: NavItem[] = [
  {
    to: '/agenda',
    label: 'Ajanda',
    icon: CalendarDays,
    badgeKey: 'agenda',
    testId: 'nav-agenda',
    permission: { module: 'agenda', action: 'view' },
  },
  {
    to: '/punch-list',
    label: 'Kusur Listesi',
    icon: AlertTriangle,
    testId: 'nav-punch-list',
    permission: { module: 'punch_list', action: 'view' },
  },
  {
    to: '/attendance',
    label: 'Puantaj',
    icon: Clock,
    testId: 'nav-attendance',
    permission: { module: 'attendance', action: 'view' },
  },
  {
    to: '/leaves',
    label: 'İzinler',
    icon: Palmtree,
    badgeKey: 'leaves',
    testId: 'nav-leaves',
    permission: { module: 'leaves', action: 'view' },
  },
  {
    to: '/meetings',
    label: 'Toplantılar',
    icon: Presentation,
    testId: 'nav-meetings',
    permission: { module: 'meetings', action: 'view' },
  },
  {
    to: '/inspections',
    label: 'Denetimler',
    icon: ClipboardCheck,
    testId: 'nav-inspections',
    permission: { module: 'inspections', action: 'view' },
  },
  {
    to: '/concrete',
    label: 'Beton Kalite',
    icon: Layers,
    testId: 'nav-concrete',
    permission: { module: 'concrete', action: 'view' },
  },
  {
    to: '/schedule',
    label: 'İş Programı',
    icon: GitBranch,
    testId: 'nav-schedule',
    permission: { module: 'tasks', action: 'view' },
  },
  {
    to: '/stock',
    label: 'Stok',
    icon: Package,
    badgeKey: 'stock',
    testId: 'nav-stock',
    permission: { module: 'stock', action: 'view' },
  },
  {
    to: '/subcontractors',
    label: 'Taşeronlar',
    icon: HandCoins,
    testId: 'nav-subcontractors',
    permission: { module: 'subcontractors', action: 'view' },
  },
  {
    to: '/price-indices',
    label: 'Fiyat Endeksleri',
    icon: TrendingUp,
    testId: 'nav-price-indices',
    permission: { module: 'subcontractors', action: 'view' },
  },
  {
    to: '/public-billing-deadlines',
    label: 'Kamu Hakediş Süreleri',
    icon: Hourglass,
    testId: 'nav-public-billing-deadlines',
    permission: { module: 'subcontractors', action: 'view' },
  },
  {
    to: '/drawings',
    label: 'Çizimler',
    icon: DraftingCompass,
    testId: 'nav-drawings',
    permission: { module: 'drawings', action: 'view' },
  },
  {
    to: '/rfis',
    label: 'RFI',
    icon: FileQuestionMark,
    testId: 'nav-rfis',
    permission: { module: 'rfi', action: 'view' },
  },
  {
    to: '/transmittals',
    label: 'Transmittal',
    icon: Files,
    testId: 'nav-transmittals',
    permission: { module: 'transmittals', action: 'view' },
  },
  {
    to: '/submittals',
    label: 'Submittal',
    icon: FileCheck,
    testId: 'nav-submittals',
    permission: { module: 'submittals', action: 'view' },
  },
  {
    to: '/instructions',
    label: 'Talimatlar',
    icon: FileSignature,
    badgeKey: 'instructions',
    testId: 'nav-instructions',
    permission: { module: 'instructions', action: 'view' },
  },
  {
    to: '/orientations',
    label: 'Oryantasyon',
    icon: GraduationCap,
    badgeKey: 'orientations',
    testId: 'nav-orientations',
    permission: { module: 'orientations', action: 'view' },
  },
  {
    to: '/equipment',
    label: 'Ekipman',
    icon: Tractor,
    testId: 'nav-equipment',
    permission: { module: 'equipment', action: 'view' },
  },
  {
    to: '/equipment-time',
    label: 'Makine Puantajı',
    icon: Clock,
    testId: 'nav-equipment-time',
    permission: { module: 'equipment', action: 'view' },
  },
  {
    to: '/bids',
    label: 'Teklifler',
    icon: Scale,
    testId: 'nav-bids',
    permission: { module: 'bids', action: 'view' },
  },
  {
    to: '/safety',
    label: 'İSG',
    icon: ShieldAlert,
    testId: 'nav-safety',
    permission: { module: 'safety', action: 'view' },
  },
  {
    to: '/work-permits',
    label: 'İş İzinleri',
    icon: FileKey,
    testId: 'nav-work-permits',
    permission: { module: 'permits', action: 'view' },
  },
  {
    to: '/bbs-observations',
    label: 'BBS Gözlemleri',
    icon: Eye,
    testId: 'nav-bbs-observations',
    permission: { module: 'safety', action: 'view' },
  },
  {
    to: '/skills',
    label: 'Beceri Matrisi',
    icon: GraduationCap,
    testId: 'nav-skills',
    permission: { module: 'skills', action: 'view' },
  },
  {
    to: '/tours',
    label: '360° Tur',
    icon: Orbit,
    testId: 'nav-tours',
    permission: { module: 'tours', action: 'view' },
  },
  {
    to: '/bim',
    label: 'BIM',
    icon: Box,
    testId: 'nav-bim',
    permission: { module: 'bim', action: 'view' },
  },
  {
    to: '/checks',
    label: 'Çek/Senet',
    icon: Landmark,
    testId: 'nav-checks',
    permission: { module: 'checks', action: 'view' },
  },
  {
    to: '/expenses',
    label: 'Masraflar',
    icon: Receipt,
    testId: 'nav-expenses',
    permission: { module: 'expenses', action: 'view' },
  },
  {
    to: '/cash',
    label: 'Şahsi Kasa',
    icon: Coins,
    testId: 'nav-cash',
    badgeKey: 'cash',
    permission: { module: 'cash', action: 'view' },
  },
  {
    to: '/units',
    label: 'Daire Satış',
    icon: Home,
    testId: 'nav-units',
    permission: { module: 'unit_sales', action: 'view' },
  },
  {
    to: '/sales-leads',
    label: 'Satış CRM',
    icon: Users,
    testId: 'nav-sales-leads',
    permission: { module: 'unit_sales', action: 'view' },
  },
  {
    to: '/estimates',
    label: 'Keşif & Poz',
    icon: Calculator,
    testId: 'nav-estimates',
    permission: { module: 'estimates', action: 'view' },
  },
  {
    // A4: ERP hesap eşleştirmeleri — backend'de admin/yonetici veya dinamik rolde erp.view
    to: '/erp-mappings',
    label: 'ERP Eşleştirme',
    icon: Settings2,
    testId: 'nav-erp-mappings',
    permission: { module: 'erp', action: 'view' },
  },
  {
    to: '/cash-flow',
    label: 'Nakit Akışı',
    icon: Wallet,
    testId: 'nav-cash-flow',
    permission: { module: 'checks', action: 'view' },
  },
  {
    to: '/mali-takvim',
    label: 'Mali Takvim',
    icon: CalendarDays,
    testId: 'nav-mali-takvim',
    permission: { module: 'dashboard', action: 'view' },
  },
  {
    to: '/mali-haberler',
    label: 'Mali Haberler',
    icon: Megaphone,
    testId: 'nav-mali-haberler',
    permission: { module: 'dashboard', action: 'view' },
  },
  {
    to: '/worker-documents',
    label: 'Belgeler',
    icon: IdCard,
    testId: 'nav-worker-documents',
    permission: { module: 'worker_documents', action: 'view' },
  },
  {
    to: '/guarantees',
    label: 'Teminatlar',
    icon: ShieldCheck,
    testId: 'nav-guarantees',
    permission: { module: 'guarantees', action: 'view' },
  },
  {
    to: '/delivery',
    label: 'Teslim Belgeleri',
    icon: PackageCheck,
    testId: 'nav-delivery',
    permission: { module: 'guarantees', action: 'view' },
  },
  {
    to: '/compliance',
    label: 'Uyum Dosyası',
    icon: FileCheck,
    testId: 'nav-compliance',
    permission: { module: 'compliance', action: 'view' },
  },
  {
    to: '/santiye-m',
    label: 'Şantiye-M',
    icon: HardHat,
    testId: 'nav-santiye-m',
    permission: { module: 'site_diary', action: 'view' },
  },
  {
    to: '/copq',
    label: 'COPQ',
    icon: CircleDollarSign,
    testId: 'nav-copq',
    permission: { module: 'copq', action: 'view' },
  },
  {
    to: '/evm',
    label: 'EVM',
    icon: LineChart,
    testId: 'nav-evm',
    permission: { module: 'evm', action: 'view' },
  },
  {
    to: '/material-requests',
    label: 'Malzeme',
    icon: PackageSearch,
    testId: 'nav-material-requests',
    permission: { module: 'material_requests', action: 'view' },
  },
  {
    to: '/change-orders',
    label: 'İş Artış/Eksiliş',
    icon: ArrowLeftRight,
    testId: 'nav-change-orders',
    permission: { module: 'change_orders', action: 'view' },
  },
  {
    to: '/toolbox-talks',
    label: 'Toolbox Talk',
    icon: Megaphone,
    testId: 'nav-toolbox-talks',
    permission: { module: 'toolbox_talks', action: 'view' },
  },
  {
    to: '/sgk-labor',
    label: 'SGK İşçilik',
    icon: Calculator,
    testId: 'nav-sgk-labor',
    permission: { module: 'sgk', action: 'view' },
  },
  {
    to: '/ai-assistant',
    label: 'AI Asistan',
    icon: Sparkles,
    testId: 'nav-ai-assistant',
    fixedRoles: ['admin', 'yonetici', 'sef'],
  },
  {
    to: '/daily-reports',
    label: 'Şantiye Defteri',
    icon: NotebookText,
    badgeKey: 'site_timeline',
    testId: 'nav-daily-reports',
    permission: { module: 'site_diary', action: 'view' },
  },
  { to: '/reminders', label: 'Hatırlatıcı', icon: Bell, badgeKey: 'reminders', testId: 'nav-reminders' },
  { to: '/release-notes', label: 'Güncellemeler', icon: Megaphone, testId: 'nav-release-notes' },
  { to: '/support', label: 'Destek', icon: Headphones, testId: 'nav-support' },
  { to: '/settings', label: 'Ayarlar', icon: Settings, testId: 'nav-settings' },
];

const adminItems: NavItem[] = [
  { to: '/projects', label: 'Projeler', icon: Briefcase, badgeKey: 'projects', testId: 'nav-projects' },
  { to: '/users', label: 'Kullanıcılar', icon: Users, testId: 'nav-users' },
  { to: '/user-groups', label: 'Gruplar', icon: UsersRound, testId: 'nav-user-groups' },
  { to: '/roles', label: 'Roller', icon: Shield, testId: 'nav-roles' },
  { to: '/reports', label: 'Raporlar', icon: FileText, testId: 'nav-reports' },
  { to: '/bug-reports', label: 'Hatalar', icon: Bug, testId: 'nav-bug-reports' },
  { to: '/feature-requests', label: 'Istekler', icon: Lightbulb, testId: 'nav-feature-requests' },
];

/**
 * "Daha Fazla" menüsü öğelerinin grup atamaları (Sidebar G1–G9 ile aynı;
 * `to` → navGroups.ts grup id'si). Hub'a birleşen öğeler (Zaman Çizelgesi,
 * Sesli Notlar → Şantiye Defteri; Lookahead → Görevler görünümü) mobilde de
 * ayrı öğe olarak listelenmez; hub kendi sekmelerini taşır.
 */
const moreItemGroups: Record<string, string> = {
  '/agenda': 'ana-alan',
  '/daily-reports': 'saha-operasyonu',
  '/punch-list': 'saha-operasyonu',
  '/attendance': 'saha-operasyonu',
  '/leaves': 'saha-operasyonu',
  '/meetings': 'saha-operasyonu',
  '/inspections': 'saha-operasyonu',
  '/concrete': 'saha-operasyonu',
  '/schedule': 'saha-operasyonu',
  '/safety': 'isg-uyum',
  '/work-permits': 'isg-uyum',
  '/bbs-observations': 'isg-uyum',
  '/skills': 'isg-uyum',
  '/toolbox-talks': 'isg-uyum',
  '/worker-documents': 'isg-uyum',
  '/orientations': 'isg-uyum',
  '/instructions': 'isg-uyum',
  '/subcontractors': 'finans-satis',
  '/price-indices': 'finans-satis',
  '/public-billing-deadlines': 'finans-satis',
  '/change-orders': 'finans-satis',
  '/checks': 'finans-satis',
  '/expenses': 'finans-satis',
  '/cash': 'finans-satis',
  '/guarantees': 'finans-satis',
  '/delivery': 'finans-satis',
  '/compliance': 'isg-uyum',
  '/santiye-m': 'isg-uyum',
  '/cash-flow': 'finans-satis',
  '/mali-takvim': 'finans-satis',
  '/mali-haberler': 'finans-satis',
  '/units': 'finans-satis',
  '/sales-leads': 'finans-satis',
  '/erp-mappings': 'finans-satis',
  '/drawings': 'teknik-ofis',
  '/estimates': 'teknik-ofis',
  '/bim': 'teknik-ofis',
  '/tours': 'teknik-ofis',
  '/rfis': 'teknik-ofis',
  '/transmittals': 'teknik-ofis',
  '/submittals': 'teknik-ofis',
  '/stock': 'tedarik-ekipman',
  '/material-requests': 'tedarik-ekipman',
  '/equipment': 'tedarik-ekipman',
  '/equipment-time': 'tedarik-ekipman',
  '/bids': 'tedarik-ekipman',
  '/reports': 'analiz-ai',
  '/copq': 'analiz-ai',
  '/evm': 'analiz-ai',
  '/sgk-labor': 'analiz-ai',
  '/ai-assistant': 'analiz-ai',
  '/projects': 'yonetim',
  '/users': 'yonetim',
  '/user-groups': 'yonetim',
  '/roles': 'yonetim',
  '/bug-reports': 'yonetim',
  '/feature-requests': 'yonetim',
  '/reminders': 'hesap',
  '/release-notes': 'hesap',
  '/support': 'hesap',
  '/settings': 'hesap',
};

export function MobileBottomNav() {
  const { user } = useAuth();
  const { selectedProject } = useProject();
  const { getCount, markRead } = useMenuNotifications(user?.id);
  const isAdmin = user?.role === 'admin';
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handleResize = () => {
      setIsKeyboardOpen(vv.height < window.innerHeight * 0.75);
    };
    vv.addEventListener('resize', handleResize);
    handleResize();
    return () => vv.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!moreOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreOpen]);

  const hasItemPermission = (item: NavItem) =>
    !item.permission || hasPermission(user, item.permission.module, item.permission.action);

  const navItems: NavItem[] = mainItems.filter(hasItemPermission);

  const moreItems: NavItem[] = (isAdmin
    ? [...moreItemsBase, ...adminItems]
    : moreItemsBase
  )
    .filter(hasItemPermission)
    .filter((item) => !item.fixedRoles || (user?.role && item.fixedRoles.includes(user.role)))
    .map((item) => {
    if (item.to === '/punch-list' || item.to === '/attendance' || item.to === '/inspections') {
      return {
        ...item,
        to: selectedProject ? `/projects/${selectedProject.id}${item.to}` : '/projects',
        disabled: !selectedProject,
      };
    }
    return item;
  });

  if (isKeyboardOpen) return null;

  /** Görünür more öğelerini grup sırasına göre kümele (grup atanmamışsa sonda, başlıksız). */
  const groupedMoreItems: { id: string; label: string; items: NavItem[] }[] = [];
  for (const group of MOBILE_NAV_GROUPS) {
    const items = moreItems.filter((item) => moreItemGroups[item.to] === group.id);
    if (items.length > 0) groupedMoreItems.push({ ...group, items });
  }
  const ungroupedItems = moreItems.filter((item) => !moreItemGroups[item.to]);
  if (ungroupedItems.length > 0) groupedMoreItems.push({ id: 'diger', label: 'Diğer', items: ungroupedItems });

  const renderItem = (item: NavItem, isMore = false) => {
    const Icon = item.icon;
    const count = item.badgeKey ? getCount(item.badgeKey) : 0;
    const testId = item.testId || `nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`;

    if (item.disabled) {
      return (
        <span
          key={testId}
          data-testid={testId}
          aria-label={`${item.label} (önce proje seçin)`}
          title="Önce bir proje seçin"
          className={`relative flex flex-none flex-col items-center justify-center gap-0.5 rounded-md px-1.5 py-1 text-[10px] font-semibold text-slate-400 sm:px-2 sm:text-xs ${
            isMore ? 'w-full' : ''
          }`}
        >
          <Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" strokeWidth={2} />
          <span className="max-w-[3.25rem] truncate whitespace-nowrap sm:max-w-[3.5rem]">{item.label}</span>
        </span>
      );
    }

    return (
      <NavLink
        key={testId}
        to={item.to}
        data-testid={testId}
        aria-label={item.label}
        title={item.label}
        onClick={() => {
          markRead(item.badgeKey);
          if (isMore) setMoreOpen(false);
        }}
        className={({ isActive }) =>
          `relative flex flex-none flex-col items-center justify-center gap-0.5 rounded-md px-1.5 py-1 text-[10px] font-semibold transition-colors sm:px-2 sm:text-xs ${
            isActive ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'
          } ${isMore ? 'w-full' : ''}`
        }
      >
        <span className="relative">
          <Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" strokeWidth={2} />
          {count > 0 && (
            <span
              data-testid={`mobile-badge-${item.badgeKey}`}
              className="absolute -right-1.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-bold text-white"
            >
              {count > 99 ? '99+' : count}
            </span>
          )}
        </span>
        <span className="max-w-[3.25rem] truncate whitespace-nowrap sm:max-w-[3.5rem]">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <>
      <nav
        data-testid="mobile-bottom-nav"
        aria-label="Mobil ana navigasyon"
        className="fixed bottom-0 left-0 right-0 z-40 h-12 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] sm:h-14 lg:hidden"
        style={{ '--mobile-nav-height': '3rem' } as React.CSSProperties}
      >
        <div className="flex h-full items-center gap-0 overflow-x-auto px-1 scrollbar-hide sm:px-2">
          {navItems.map((item) => renderItem(item))}
          <button
            type="button"
            data-testid="nav-more"
            aria-label="Daha Fazla"
            title="Daha Fazla"
            onClick={() => setMoreOpen((v) => !v)}
            className={`relative flex flex-none flex-col items-center justify-center gap-0.5 rounded-md px-1.5 py-1 text-[10px] font-semibold transition-colors sm:px-2 sm:text-xs ${
              moreOpen ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <MoreHorizontal className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" strokeWidth={2} />
            <span className="max-w-[3.25rem] truncate whitespace-nowrap sm:max-w-[3.5rem]">Daha Fazla</span>
          </button>
        </div>
      </nav>

      {moreOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" aria-hidden="true" />
          <div
            ref={panelRef}
            data-testid="mobile-more-menu"
            className="fixed bottom-[calc(3rem+env(safe-area-inset-bottom))] left-2 right-2 z-50 rounded-t-2xl rounded-b-xl bg-white p-4 shadow-2xl ring-1 ring-slate-200 sm:bottom-[calc(3.5rem+env(safe-area-inset-bottom))] lg:hidden"
          >
            <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2">
              <span className="text-sm font-semibold text-slate-800">Daha Fazla</span>
              <button
                type="button"
                data-testid="mobile-more-menu-close"
                onClick={() => setMoreOpen(false)}
                className="rounded-full p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Kapat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[60vh] space-y-3 overflow-y-auto">
              {groupedMoreItems.map((group) => (
                <div key={group.id}>
                  <p
                    data-testid={`mobile-nav-group-${group.id}`}
                    className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
                  >
                    {group.label}
                  </p>
                  <div className="grid grid-cols-3 content-start items-start justify-start gap-y-2">
                    {group.items.map((item) => renderItem(item, true))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
